import {useTranslation} from 'react-i18next';
import {BookOpen} from 'lucide-react';
import {AssistantPanel, cn, useFitToHeight} from '@chekinapp/ui';
import {GuidebookIcon} from '@chekin/shared/guidebooks';
import {useSummary} from 'context/summary';
import type {VelaHeaderCopy} from '../copy/velaCopy';
import {useVelaGuidebookNavState} from '../hooks/useVelaGuidebookNavStore';
import {SectionIndex} from './SectionIndex';
import {VelaHeader} from './VelaHeader';
import {VelaTray} from './VelaTray';

const FIT_LEVELS = 5;

type Props = {
  header: VelaHeaderCopy;
  id?: string;
  className?: string;
};

function VelaGuidebookNav({header, id, className}: Props) {
  const {t} = useTranslation();
  const {housingName} = useSummary();
  const chapters = useVelaGuidebookNavState(state => state.chapters);
  const siblings = useVelaGuidebookNavState(state => state.siblings);
  const activeId = useVelaGuidebookNavState(state => state.activeId);
  const onSelectChapter = useVelaGuidebookNavState(state => state.onSelectChapter);
  const onSelectSibling = useVelaGuidebookNavState(state => state.onSelectSibling);
  const fit = useFitToHeight<HTMLElement>({levels: FIT_LEVELS});

  return (
    <AssistantPanel
      ref={fit.ref}
      id={id}
      className={cn(className, 'overflow-hidden pb-8')}
    >
      <VelaHeader name={header.name} status={header.status} />

      {chapters.length > 0 && (
        <SectionIndex
          variant="sections"
          title={t('guidebook_on_this_page')}
          hint={t('guidebook_on_this_page_hint')}
          className="mb-[18px] mt-1.5"
        >
          {chapters.map((chapter, index) => (
            <SectionIndex.Item
              key={chapter.id}
              index={index + 1}
              active={chapter.id === activeId}
              onClick={() => onSelectChapter?.(chapter.id)}
            >
              {chapter.title}
            </SectionIndex.Item>
          ))}
        </SectionIndex>
      )}

      {siblings.length > 0 && (
        <VelaTray>
          <SectionIndex
            variant="links"
            title={t('guidebook_other_guidebooks')}
            hint={t('guidebook_other_guidebooks_hint', {
              propertyName: housingName || t('guidebooks_description_fallback_name'),
            })}
          >
            {siblings.map(guidebook => (
              <SectionIndex.Link
                key={guidebook.id}
                icon={<GuidebookIcon name={guidebook.icon} fallback={BookOpen} />}
                title={guidebook.title}
                description={guidebook.description || undefined}
                onClick={() => onSelectSibling?.(guidebook)}
              />
            ))}
          </SectionIndex>
        </VelaTray>
      )}
    </AssistantPanel>
  );
}

export {VelaGuidebookNav};
